import { useEffect, useRef, useCallback, useMemo } from 'react';
import { FilterOperator, FilterOperators } from './FilterOperators';
import { guardedSync, useStableKey } from '../shared';

// ── Types ──────────────────────────────────────────────────────────────

/**
 * Minimal subset of the Abyss `useForm` return value that this hook
 * needs in order to read and write form field values.
 *
 * Compatible with `@abyss/web/hooks/useForm` (react-hook-form based).
 */
export interface FormInstance {
  /** Reactive subscription to one or all form fields. */
  watch: {
    (name: string): unknown;
    (): Record<string, unknown>;
  };
  /** Imperatively read the current value of a field (or all fields). */
  getValues: {
    (name: string): unknown;
    (): Record<string, unknown>;
  };
  /** Imperatively set a field value. */
  setValue: (name: string, value: unknown, options?: { shouldValidate?: boolean; shouldDirty?: boolean }) => void;
}

/**
 * A single condition entry inside a DataTable filter value (v1).
 *
 * `state.filters[n].value` is an **array** of these objects.
 */
export interface FilterCondition {
  condition: string;
  filterValue: unknown;
  initialValue?: unknown;
}

/**
 * Minimal subset of the `useDataTable` return value that this hook
 * needs in order to read and write the DataTable's column filters.
 */
export interface FilterableTableState {
  state: {
    /**
     * Current column filters.
     *
     * Each entry is `{ id: string; value: FilterCondition[] }` where
     * `value` is an array of condition objects, **not** a plain string.
     */
    filters: Array<{ id: string; value: FilterCondition[] }>;
  };
  filter: {
    /** Set a single column filter by accessor. */
    setFilter: (columnId: string, filterValue: unknown) => void;
    /** Replace all column filters at once. */
    setAllFilters: (
      updater:
        | Array<{ id: string; value: unknown }>
        | ((prev: Array<{ id: string; value: unknown }>) => Array<{ id: string; value: unknown }>),
    ) => void;
  };
}

/**
 * Configuration for `useFormGridFilterSync`.
 */
export interface UseFormGridFilterSyncOptions {
  /** The Abyss `useForm` instance. */
  form: FormInstance;
  /** The `useDataTable` return value. */
  tableState: FilterableTableState;
  /**
   * The form field names to synchronize.
   *
   * Each name is matched against the DataTable filter `id` (column accessor).
   * Only listed fields participate in the sync.
   */
  fields: string[];
  /**
   * The default operator used when pushing a form value into the
   * DataTable filter.  Defaults to `FilterOperators.contains`.
   *
   * Supply any `FilterOperator` — built-in or custom.
   */
  defaultOperator?: FilterOperator;
  /**
   * Per-field operator overrides.
   *
   * Keys are field names; values are the `FilterOperator` to use for
   * that field instead of `defaultOperator`.
   *
   * @example
   * ```ts
   * fieldOperators: {
   *   age: FilterOperators.greaterThan,
   *   status: FilterOperators.equals,
   * }
   * ```
   */
  fieldOperators?: Record<string, FilterOperator>;
}

/**
 * Return value of `useFormGridFilterSync`.
 */
export interface UseFormGridFilterSyncResult {
  /**
   * Push the current form field values into the DataTable column
   * filters.  Call this from a button handler (e.g. "Filter") so that
   * filters are only applied when the user explicitly requests it —
   * **not** reactively while they are still editing the form.
   */
  ApplyFormFilters: () => void;
}

// ── Helpers ────────────────────────────────────────────────────────────

/** Stable JSON representation for shallow equality checks. */
function toKey(value: unknown): string {
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}

/** @internal Re-export for inline use where the hook version isn't suitable. */
const toStableKey = toKey;

/**
 * Determines whether a value is "empty" and should be treated as
 * the absence of a filter (i.e. the filter should be removed).
 */
function isEmptyValue(value: unknown): boolean {
  if (value === undefined || value === null || value === '') return true;
  if (Array.isArray(value) && value.length === 0) return true;
  return false;
}

/**
 * Convert a plain form value into the DataTable's filter condition array
 * format: `[{ condition, filterValue }]`.
 */
function toFilterConditionArray(
  value: unknown,
  condition: string,
): FilterCondition[] {
  return [{ condition, filterValue: value }];
}

/**
 * Extract the first meaningful `filterValue` from a DataTable filter's
 * condition array.  Returns `''` when the array is empty or missing.
 */
function fromFilterConditionArray(conditions: FilterCondition[] | undefined): unknown {
  if (!conditions || !Array.isArray(conditions) || conditions.length === 0) {
    return '';
  }
  return conditions[0].filterValue ?? '';
}

// ── Hook ───────────────────────────────────────────────────────────────

/**
 * On-demand synchronization between an Abyss `useForm` form instance
 * and a `useDataTable` filterDataSet.
 *
 * **Form → Grid (explicit):**  Call the returned `ApplyFormFilters()`
 * method to read each listed field from the form and push its value
 * into the corresponding DataTable column filter.  No sync occurs
 * while the user is editing the form — filters are only applied when
 * you invoke `ApplyFormFilters`.
 *
 * **Grid → Form (reactive):**  When a DataTable column filter listed
 * in `fields` changes (e.g. the user interacts with the filterDataSet
 * modal), the hook extracts the first `filterValue` from the condition
 * array and writes it back to the form via `form.setValue`.
 *
 * Operators are fully externalized via `FilterOperators` / custom
 * `FilterOperator` objects.  Use `defaultOperator` and `fieldOperators`
 * to control which condition string is applied per field.
 *
 * @example
 * ```tsx
 * import { FilterOperators } from './FilterOperators';
 *
 * const form = useForm({ defaultValues: { name: '', age: '' } });
 * const tableState = useDataTable({ ... });
 *
 * const { ApplyFormFilters } = useFormGridFilterSync({
 *   form,
 *   tableState,
 *   fields: ['name', 'age'],
 *   fieldOperators: { age: FilterOperators.greaterThan },
 * });
 *
 * <Button onClick={ApplyFormFilters}>Filter</Button>
 * ```
 */
export function useFormGridFilterSync(
  options: UseFormGridFilterSyncOptions,
): UseFormGridFilterSyncResult {
  const {
    form,
    tableState,
    fields,
    defaultOperator = FilterOperators.contains,
    fieldOperators = {},
  } = options;

  // Guard: tracks which side is currently pushing an update so the
  // receiving side can ignore the resulting change and avoid loops.
  const syncSourceRef = useRef<'form' | 'grid' | null>(null);

  // Snapshot of the last-known grid values for each field so we can
  // detect actual changes (avoids re-syncing when nothing meaningful
  // changed).
  const lastGridValuesRef = useRef<Record<string, string>>({});

  // Build the field → operator lookup once (re-computed only when the
  // inputs change), avoiding repeated iteration on every ApplyFormFilters call.
  const fieldOperatorMap = useMemo(() => {
    return fields.reduce<Record<string, FilterOperator>>((map, field) => {
      map[field] = fieldOperators[field] ?? defaultOperator;
      return map;
    }, {});
  }, [fields, fieldOperators, defaultOperator]);

  // ── Form → Grid (on-demand) ────────────────────────────────────────

  const ApplyFormFilters = useCallback(() => {
    guardedSync(syncSourceRef, 'form', () => {
      for (const field of fields) {
        const value = form.getValues(field);
        const operator = fieldOperatorMap[field];

        if (isEmptyValue(value)) {
          // Remove the filter when the form field is cleared.
          tableState.filter.setFilter(field, undefined);
        } else {
          // Wrap the plain value in the condition-array format the
          // DataTable expects: [{ condition, filterValue }].
          tableState.filter.setFilter(
            field,
            toFilterConditionArray(value, operator.condition),
          );
        }
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form, tableState, fields, fieldOperatorMap]);

  // ── Grid → Form (reactive) ────────────────────────────────────────
  const filtersKey = useStableKey(tableState.state.filters);

  useEffect(() => {
    if (syncSourceRef.current === 'form') {
      syncSourceRef.current = null;
      return;
    }

    // Build a lookup: column accessor → condition array.
    const filterMap = new Map<string, FilterCondition[]>();
    for (const entry of tableState.state.filters) {
      filterMap.set(entry.id, entry.value);
    }

    let didChange = false;

    for (const field of fields) {
      const conditions = filterMap.get(field);
      // Extract the plain value from the first condition entry.
      const gridValue = fromFilterConditionArray(conditions);
      const gridKey = toKey(gridValue);

      if (gridKey !== lastGridValuesRef.current[field]) {
        didChange = true;
        lastGridValuesRef.current[field] = gridKey;
      }
    }

    if (!didChange) return;

    guardedSync(syncSourceRef, 'grid', () => {
      for (const field of fields) {
        const conditions = filterMap.get(field);
        const gridValue = fromFilterConditionArray(conditions);
        const formValue = form.getValues(field);

        if (toKey(gridValue) !== toKey(formValue)) {
          form.setValue(field, gridValue, { shouldDirty: false });
        }
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtersKey]);

  return { ApplyFormFilters };
}
